import type {
  AdditionalPaymentMode,
  CampaignDetailsCreator,
  CampaignStats,
} from '@metapic-apps/shared'

import { normalizeMoney, serializeMoney } from './common.adapter'

import { normalizeImage } from '@/lib/adapters/image.adapter'
import { normalizeSharedBudget } from '@/lib/adapters/shared-budgets.adapter'
import {
  handleInvoiceValues,
  normalizeTrafficSourcesCosts,
  serializeTrafficSourceCosts,
} from '@/lib/adapters/traffic-source-costs.adapter'
import { formatLocalDateToUtcWithoutOffset, hasItems } from '@/lib/helpers'
import {
  type CampaignCreationPayloadType,
  type CampaignDetailsCreatorsListPayloadType,
  type CampaignFormSerializedDataType,
  type CampaignFormType,
  type CampaignServerType,
  type CampaignStatsPayloadType,
  type CampaignType,
  CampaignTypeEnum,
  PreregisterItemEnum,
  type PreregisterItemType,
  type TargetsServerType,
  type TodoListType,
} from '@/lib/types/campaign.types'
import { TargetItemEnum, type TargetItemType } from '@/lib/types/common.types'

export const normalizeCampaign = (
  data: CampaignServerType,
  targets?: TargetsServerType,
): CampaignType => {
  const costCreator = data.additional_payment?.cost_creator
    ? normalizeMoney(data.additional_payment.cost_creator).amount
    : undefined
  const revenueInvoice = data.additional_payment?.revenue_invoice
    ? normalizeMoney(data.additional_payment.revenue_invoice).amount
    : undefined

  return {
    advertiserGroup: data.store_group_id,
    advertiserId: data.store_id,
    campaignType: data.type,
    internalName: data.name,
    advertiserList: [
      {
        id: data.store_id,
        label: data.store_name,
        ...(data.store_logo && { logo: normalizeImage(data.store_logo) }),
      },
    ],
    hasTargetAllCreators:
      !targets?.client_ids?.length &&
      !targets?.user_ids?.length &&
      !targets?.user_tag_ids?.length &&
      !targets?.revenue_tier_ids?.length &&
      !targets?.segment_ids?.length &&
      !targets?.segments?.length,
    hasPreregisterCreators: Boolean(
      targets?.emails?.length || targets?.social_media_identifiers?.length,
    ),
    hasProductSeeding: data.has_product_seeding,
    isShownForAdvertiser: data.show_for_advertiser,
    hasTodoItems: Boolean(data.todo?.length),
    hasBadge: Boolean(data.campaign_title || data.campaign_text),
    title: data.campaign_title || '',
    description: data.campaign_text || '',
    invoiceAdvertiserDirectly: Boolean(
      data.traffic_sources_costs?.filter((source) => source.invoice_cpa || source.invoice_cpc_v2)
        .length,
    ),
    hasCommission: Boolean(data.traffic_sources_costs?.length),
    hasAdditionalPayment: Boolean(data.additional_payment),
    hasInvoiceAdvertiserDirectly: data.additional_payment?.mode === 'INDEPENDENTLY_SET',
    hasStartNow: Boolean(!data.constraints.valid_from),
    hasOpenEnd: Boolean(
      !data.constraints.valid_until &&
      !data.constraints.per_user_limit &&
      !data.constraints.max_clicks &&
      !data.shared_budget,
    ),
    perUserLimit: data.constraints.per_user_limit,
    hashtags: data.hashtags ?? [],
    todoList: data.todo?.length ? normalizeTodoList(data.todo) : [{ key: 0, value: '' }],
    targets: targets && hasItems(targets) ? normalizeTargets(targets).targets : [],
    currency: data.currency,
    preregisterItems:
      targets && hasItems(targets) ? normalizeTargets(targets).preregisterItems : [],
    trafficSourceCosts: normalizeTrafficSourcesCosts(
      data.traffic_sources_costs ?? [],
      data.currency,
    ),
    hasBudget: Boolean(data.shared_budget) || Boolean(data.budget_limit),
    ...(data.shared_budget && { sharedBudget: normalizeSharedBudget(data.shared_budget) }),
    ...(data.image && { image: normalizeImage(data.image) }),
    ...(costCreator && { creatorCost: costCreator }),
    ...(revenueInvoice !== undefined && { revenueInvoice: revenueInvoice }),
    ...(data.constraints.valid_from && { validFrom: data.constraints.valid_from }),
    ...(data.constraints.valid_until && { validUntil: data.constraints.valid_until }),
    ...(data.constraints.current_clicks && { currentClicks: data.constraints.current_clicks }),
    ...(data.constraints.max_clicks && { maxClicks: data.constraints.max_clicks }),
    ...(data.consumed_budget?.amount && {
      currentBudgetSpent: Number(data.consumed_budget.amount),
    }),
    ...(data.budget_limit && { budgetLimit: data.budget_limit / 100 }),
    ...(data.id && {
      extra: {
        token: data.token,
        id: data.id,
        status: data.status,
      },
    }),
  }
}

export const normalizeTodoList = (list: string[]): TodoListType =>
  list.map((item, id) => ({
    key: id,
    value: item,
  }))

export const normalizeTargets = (
  data: TargetsServerType,
): { targets: TargetItemType[]; preregisterItems: PreregisterItemType[] } => {
  const targets: TargetItemType[] = []
  const preregisterItems: PreregisterItemType[] = []

  if (data.users) {
    data.users.forEach((user) =>
      targets.push({
        type: TargetItemEnum.User,
        id: user.id,
        label: `[ID: ${user.id}] @${user.username || ''}`,
      }),
    )
  }
  if (data.user_tags) {
    data.user_tags.forEach((tag) =>
      targets.push({ type: TargetItemEnum.UserTag, id: tag.id, label: `${tag.name}` }),
    )
  }
  if (data.clients) {
    data.clients.forEach((client) =>
      targets.push({ type: TargetItemEnum.Client, id: client.id, label: `${client.name}` }),
    )
  }
  if (data.revenue_tiers) {
    data.revenue_tiers.forEach((tier) =>
      targets.push({
        type: TargetItemEnum.RevenueTier,
        id: tier.id,
        label: `${tier.client_name}: ${tier.name}`,
      }),
    )
  }
  if (data.segments) {
    data.segments.forEach((segment) =>
      targets.push({ type: TargetItemEnum.Segment, id: segment.id, label: segment.name }),
    )
  }
  if (data.emails) {
    data.emails.forEach((identifier) =>
      preregisterItems.push({ type: PreregisterItemEnum.Email, identifier }),
    )
  }
  if (data.social_media_identifiers) {
    data.social_media_identifiers.forEach((identifier) =>
      preregisterItems.push({
        type: PreregisterItemEnum.SocialMediaUser,
        identifier,
        label: identifier.startsWith('@') ? identifier : `@${identifier}`,
      }),
    )
  }

  return { targets, preregisterItems }
}

export const serializeTargets = ({
  targets,
  preregisterItems,
  advertiserGroup,
  hasTargetAllCreators,
}: {
  targets: TargetItemType[]
  preregisterItems: PreregisterItemType[]
  advertiserGroup: number
  hasTargetAllCreators: boolean
}): CampaignCreationPayloadType['targets'] | undefined => {
  const numericIdsOf = (type: TargetItemEnum) =>
    targets.filter((item) => item.type === type).map((item) => Number(item.id))
  const users = numericIdsOf(TargetItemEnum.User)
  const userTags = numericIdsOf(TargetItemEnum.UserTag)
  const clientIds = numericIdsOf(TargetItemEnum.Client)
  const revenueTiers = numericIdsOf(TargetItemEnum.RevenueTier)
  const segmentIds = targets
    .filter((item) => item.type === TargetItemEnum.Segment)
    .map((item) => String(item.id))
  const emails = preregisterItems
    .filter((item) => item.type === PreregisterItemEnum.Email)
    .map((item) => item.identifier)
  const socialProfiles = preregisterItems
    .filter((item) => item.type === PreregisterItemEnum.SocialMediaUser)
    .map((item) => item.identifier)

  const results = {
    ...(hasTargetAllCreators && { store_group_ids: [advertiserGroup] }),
    ...(!hasTargetAllCreators && users.length && { user_ids: users }),
    ...(!hasTargetAllCreators && userTags.length && { user_tag_ids: userTags }),
    ...(!hasTargetAllCreators && clientIds.length && { client_ids: clientIds }),
    ...(!hasTargetAllCreators && revenueTiers.length && { revenue_tier_ids: revenueTiers }),
    ...(!hasTargetAllCreators && segmentIds.length && { segment_ids: segmentIds }),
    ...(emails.length && { emails: emails }),
    ...(socialProfiles.length && { social_media_identifiers: socialProfiles }),
  }
  return Object.keys(results).length > 0 ? results : undefined
}

const serializeTodoList = (list: TodoListType): string[] | undefined =>
  list.length && list[0]?.value ? list.map((item) => item.value) : undefined

type SerializedSharedBudget = {
  budget_limit: CampaignCreationPayloadType['budget_limit']
  budget_limit_v2: CampaignCreationPayloadType['budget_limit_v2']
} & (
  | { shared_budget: CampaignCreationPayloadType['shared_budget']; shared_budget_id?: never }
  | { shared_budget_id: CampaignCreationPayloadType['shared_budget_id']; shared_budget?: never }
)

const serializeSharedBudget = (
  currency: string,
  hasBudget?: boolean,
  data?: CampaignFormType['sharedBudget'],
  budgetLimit?: CampaignFormType['budgetLimit'],
): SerializedSharedBudget => {
  if (!hasBudget) {
    return {
      budget_limit: null,
      budget_limit_v2: null,
      shared_budget_id: null,
    }
  }

  if (data?.isCreated) {
    return {
      shared_budget: {
        amount: data.amount.amount * 100,
        amount_v2: serializeMoney(data.amount),
        title: data.title,
      },
      budget_limit: budgetLimit ? budgetLimit * 100 : null,
      budget_limit_v2: budgetLimit ? serializeMoney({ amount: budgetLimit, currency }) : null,
    }
  }

  if (data?.id && !data.isCreated) {
    return {
      shared_budget_id: data.id,
      budget_limit: budgetLimit ? budgetLimit * 100 : null,
      budget_limit_v2: budgetLimit ? serializeMoney({ amount: budgetLimit, currency }) : null,
    }
  }

  return {
    budget_limit: budgetLimit ? budgetLimit * 100 : null,
    budget_limit_v2: budgetLimit ? serializeMoney({ amount: budgetLimit, currency }) : null,
    shared_budget_id: null,
  }
}

export const serializeCampaign = (
  data: CampaignFormType,
  paymentMode: AdditionalPaymentMode,
): CampaignFormSerializedDataType => {
  let image
  if (data.tempCampaignImage) {
    image = new FormData()
    image.append('default', data.tempCampaignImage)
  }

  return {
    campaigns: data.advertiserList.map(({ id }) => ({
      ...(data.advertiserGroup && { store_group_id: data.advertiserGroup }),
      store_id: id,
      type: data.campaignType,
      has_product_seeding: data.hasProductSeeding,
      targets: serializeTargets({
        targets: data.targets,
        preregisterItems: data.preregisterItems,
        advertiserGroup: data.advertiserGroup,
        hasTargetAllCreators: data.hasTargetAllCreators,
      }),
      per_user_limit: Boolean(data.perUserLimit),
      show_for_advertiser: !!data.isShownForAdvertiser,
      name: data.internalName,
      campaign_title:
        data.hasBadge || data.campaignType !== CampaignTypeEnum.Standard ? data.title : '',
      campaign_text:
        data.hasBadge || data.campaignType !== CampaignTypeEnum.Standard ? data.description : '',
      todo: data.hasTodoItems ? serializeTodoList(data.todoList) : [],
      hashtags: data.hashtags ?? [],
      traffic_sources_costs: data.hasCommission
        ? serializeTrafficSourceCosts(
            handleInvoiceValues(data.trafficSourceCosts || [], data.invoiceAdvertiserDirectly),
            data.currency,
          )
        : [],
      valid_from:
        !data.hasStartNow && data.validFrom
          ? formatLocalDateToUtcWithoutOffset(data.validFrom)
          : null,
      valid_until:
        !data.hasOpenEnd && data.validUntil
          ? formatLocalDateToUtcWithoutOffset(data.validUntil)
          : null,
      max_clicks: data.maxClicks ? Number(data.maxClicks) : null,
      ...(data.hasAdditionalPayment && data.creatorCost
        ? {
            additional_payment: {
              mode: paymentMode,
              cost_creator: serializeMoney({
                amount: Number(data.creatorCost),
                currency: data.currency,
              }),
              ...(paymentMode === 'INDEPENDENTLY_SET' &&
                data.revenueInvoice && {
                  revenue_invoice: serializeMoney({
                    amount: Number(data.revenueInvoice),
                    currency: data.currency,
                  }),
                }),
            },
          }
        : { has_onetime_payment: false }),
      ...(data.priority?.[id] !== undefined && { priority_after: data.priority[id] }),
      ...serializeSharedBudget(data.currency, data.hasBudget, data.sharedBudget, data.budgetLimit),
    })),
    ...(image && { image }),
  }
}

export const normalizeCampaignCreatorsList = (
  creators: CampaignDetailsCreatorsListPayloadType,
  currency: string,
): CampaignDetailsCreator[] => {
  return creators.map((creator) => ({
    id: creator.id,
    userId: creator.user_id,
    displayName: creator.display_name,
    linksCount: creator.links_created,
    contentCount: creator.content_created,
    clicksCount: creator.clicks,
    gpRatio: creator.estimated_earning_to_cost_ratio,
    estimatedGp: creator.estimated_gp / 100,
    estimatedMediaValue: parseFloat(creator.estimated_media_value.amount),
    invitePending: creator.status === 'open',
    ...(creator.creator_avatar && { avatar: normalizeImage(creator.creator_avatar) }),
    overrides: {
      clickLimit: creator.overrides?.click_limit ?? null,
      trafficSourcesCosts: normalizeTrafficSourcesCosts(
        creator.overrides?.traffic_sources_costs ?? [],
        currency,
      ),
    },
  }))
}

export const normalizeCampaignCreatorsCount = (stats: {
  active_users: number
  inactive_users: number
}): { activeUsers: number; inactiveUsers: number } => ({
  activeUsers: stats.active_users,
  inactiveUsers: stats.inactive_users,
})

export const normalizeCampaignStats = (stats: CampaignStatsPayloadType): CampaignStats => {
  return {
    summary: {
      linksCreated: stats.summary.links_created,
      clicks: stats.summary.clicks,
      orders: stats.summary.orders,
      orderValue: stats.summary.order_value / 100,
      estimatedCost: stats.summary.estimated_cost / 100,
      estimatedEarnings: stats.summary.estimated_earning / 100,
      estimatedGp: stats.summary.estimated_gp / 100,
      estimatedGpRatio: stats.summary.estimated_earning_to_cost_ratio,
    },
    dailyStats: stats.daily_stats.map((dayStats) => ({
      clicks: dayStats.clicks,
      estimatedCost: dayStats.estimated_cost / 100,
      estimatedEarnings: dayStats.estimated_earning / 100,
      estimatedGp: dayStats.estimated_gp / 100,
      estimatedGpRatio: dayStats.estimated_earning_to_cost_ratio,
      linksCreated: dayStats.links_created,
      orderValue: dayStats.order_value / 100,
      orders: dayStats.orders,
      startDate: dayStats.period_start_date,
      sources: dayStats.sources.map((source) => ({
        clicks: source.clicks,
        estimatedCost: source.estimated_cost / 100,
        estimatedEarnings: source.estimated_earning / 100,
        estimatedGp: source.estimated_gp / 100,
        estimatedGpRatio: source.estimated_earning_to_cost_ratio,
        orderValue: source.order_value / 100,
        orders: source.orders,
        source: source.source,
      })),
    })),
  }
}
